import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';

import { CurrencyInput } from 'components/ui/Inputs';
import ValidationForm from 'components/ui/ValidationForm';

import SplitPayment from '../../SplitPayment';
import { handlePaymentSubmit } from '../../SplitPayment/SplitPayment.utils';
import { Errors } from '../../SplitPayment/SplitPayment.styles';
import { amountValidator, additionalInformationValidator, reasonCodeValidator, labelValidator } from '../../common/WriteOff.utils';

import { WRITE_OFF_REASONS, reasonCodeToLabel } from '../../../../../constants/reasons';

import formatReason from '../../../../../utils/formatReason';

import { StyledDropdown, StyledTextArea, StyledLabel } from './WriteOffPayment.styles';

export const handleReasonChange = (changeField, resetField) => (event) => {
  const [code, label] = event.target.value.split('_');
  changeField('reasonCode')(code);
  changeField('label')(label);
  resetField('additionalExplanation');
};

const WriteOffPayment = ({
  totalPriceToBePaid,
  onAmountChange,
  currentAmount,
  isOnHold,
  intl: { formatMessage },
  payAndConfirm,
  takeDeposit,
  addPayment,
  reference,
  currency,
  method,
  loading,
  isPutOnHoldWithDeposit
}) => (
  <ValidationForm
    fields={{
      amount: currentAmount,
      reasonCode: '',
      additionalInformation: '',
      label: ''
    }}
    validators={{
      amount: (value) => amountValidator(value, totalPriceToBePaid),
      additionalInformation: additionalInformationValidator,
      reasonCode: reasonCodeValidator,
      label: labelValidator
    }}
    overrideFields={{ amount: currentAmount }}
    validateInitialFields
  >
    {({ fields, onChange, changeField, resetField, invalidInputs, hasInvalidInputs }) => {
      const { reasonCode, additionalInformation, label } = fields;

      const handleSubmit = (e) => {
        e.preventDefault();

        handlePaymentSubmit({
          cashTendered: fields.amount,
          totalPriceToBePaid,
          payAndConfirm,
          takeDeposit,
          addPayment,
          isOnHold,
          bookingReference: reference,
          currency,
          method,
          reference: `${reasonCodeToLabel(reasonCode, label)}-${new Date().getTime()}`,
          reason: formatReason(reasonCode, additionalInformation, label)
        });
      };

      const handleChange = (e) => {
        onChange(e);

        if (onAmountChange) {
          onAmountChange(e.target.value);
        }
      };

      return (
        <SplitPayment
          title={<FormattedMessage id="payments.AmountToPayByWriteOff" />}
          subTitle={<FormattedMessage id="payments.canBeSplit" />}
          label={<FormattedMessage id="payments.recordPayment" />}
          loading={loading}
          disabled={hasInvalidInputs}
          onSubmit={handleSubmit}
        >
          <CurrencyInput
            name="amount"
            value={fields.amount}
            currency={currency}
            disabled={isPutOnHoldWithDeposit ? false : isOnHold}
            onChange={handleChange}
          />

          <div>
            <StyledLabel>
              <FormattedMessage id="payments.reasonForWriteOff" />
            </StyledLabel>
            <StyledDropdown
              name="reasonCode"
              value={`${reasonCode}_${label}`}
              required
              data-cy="write-off-reason"
              onChange={handleReasonChange(changeField, resetField)}
            >
              <option value="">{formatMessage({ id: 'payments.dropdownDefault' })}</option>
              {WRITE_OFF_REASONS.map(({ code, label }) => (
                <option key={code} value={`${code}_${label}`}>
                  {label}
                </option>
              ))}
            </StyledDropdown>
          </div>

          <div>
            <StyledLabel>
              <FormattedMessage id="payments.writeOffAdditionalInfo" />
            </StyledLabel>
            <StyledTextArea
              name="additionalInformation"
              value={additionalInformation}
              rows="3"
              cols="40"
              data-cy="write-off-payment-additional-information"
              onChange={onChange}
              maxLength={37}
            />
          </div>
          <Errors>
            {invalidInputs.amount && Number(fields.amount) === 0 && <FormattedMessage id="payments.amountZero" />}
            {invalidInputs.amount && Number(fields.amount) !== 0 && <FormattedMessage id="payments.amountTooLarge" />}
          </Errors>
        </SplitPayment>
      );
    }}
  </ValidationForm>
);

WriteOffPayment.propTypes = {
  currentAmount: PropTypes.string,
  onAmountChange: PropTypes.func,
  totalPriceToBePaid: PropTypes.number,
  loading: PropTypes.bool,
  isOnHold: PropTypes.bool,
  payAndConfirm: PropTypes.func,
  takeDeposit: PropTypes.func,
  addPayment: PropTypes.func,
  reference: PropTypes.string,
  currency: PropTypes.string,
  method: PropTypes.string,
  intl: intlShape,
  isPutOnHoldWithDeposit: PropTypes.bool
};

export default WriteOffPayment;
